import React, { useState, useMemo } from 'react';
import * as FeatherIcons from 'react-icons/fi';
import {
    FaGlobe, FaFacebook, FaTwitter, FaInstagram, FaGoogle, FaApple,
    FaLinkedin, FaGithub, FaYoutube, FaTiktok
} from 'react-icons/fa';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { ChevronDown } from 'lucide-react';

const IconPicker = ({ value, onChange }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);

    // Comprehensive icon list with names and components
    const featherIcons = [
        { name: 'heart', label: 'Heart', component: FeatherIcons.FiHeart },
        { name: 'star', label: 'Star', component: FeatherIcons.FiStar },
        { name: 'check', label: 'Check', component: FeatherIcons.FiCheck },
        { name: 'check-circle', label: 'Check Circle', component: FeatherIcons.FiCheckCircle },
        { name: 'x', label: 'X', component: FeatherIcons.FiX },
        { name: 'x-circle', label: 'X Circle', component: FeatherIcons.FiXCircle },
        { name: 'plus', label: 'Plus', component: FeatherIcons.FiPlus },
        { name: 'plus-circle', label: 'Plus Circle', component: FeatherIcons.FiPlusCircle },
        { name: 'minus', label: 'Minus', component: FeatherIcons.FiMinus },
        { name: 'minus-circle', label: 'Minus Circle', component: FeatherIcons.FiMinusCircle },
        { name: 'arrow-right', label: 'Arrow Right', component: FeatherIcons.FiArrowRight },
        { name: 'arrow-left', label: 'Arrow Left', component: FeatherIcons.FiArrowLeft },
        { name: 'arrow-up', label: 'Arrow Up', component: FeatherIcons.FiArrowUp },
        { name: 'arrow-down', label: 'Arrow Down', component: FeatherIcons.FiArrowDown },
        { name: 'chevron-right', label: 'Chevron Right', component: FeatherIcons.FiChevronRight },
        { name: 'chevron-left', label: 'Chevron Left', component: FeatherIcons.FiChevronLeft },
        { name: 'chevron-up', label: 'Chevron Up', component: FeatherIcons.FiChevronUp },
        { name: 'chevron-down', label: 'Chevron Down', component: FeatherIcons.FiChevronDown },
        { name: 'user', label: 'User', component: FeatherIcons.FiUser },
        { name: 'users', label: 'Users', component: FeatherIcons.FiUsers },
        { name: 'settings', label: 'Settings', component: FeatherIcons.FiSettings },
        { name: 'home', label: 'Home', component: FeatherIcons.FiHome },
        { name: 'search', label: 'Search', component: FeatherIcons.FiSearch },
        { name: 'bell', label: 'Bell', component: FeatherIcons.FiBell },
        { name: 'menu', label: 'Menu', component: FeatherIcons.FiMenu },
        { name: 'more-vertical', label: 'More Vertical', component: FeatherIcons.FiMoreVertical },
        { name: 'more-horizontal', label: 'More Horizontal', component: FeatherIcons.FiMoreHorizontal },
        { name: 'calendar', label: 'Calendar', component: FeatherIcons.FiCalendar },
        { name: 'camera', label: 'Camera', component: FeatherIcons.FiCamera },
        { name: 'map-pin', label: 'Map Pin', component: FeatherIcons.FiMapPin },
        { name: 'music', label: 'Music', component: FeatherIcons.FiMusic },
        { name: 'video', label: 'Video', component: FeatherIcons.FiVideo },
        { name: 'message-circle', label: 'Message Circle', component: FeatherIcons.FiMessageCircle },
        { name: 'message-square', label: 'Message Square', component: FeatherIcons.FiMessageSquare },
        { name: 'mail', label: 'Mail', component: FeatherIcons.FiMail },
        { name: 'phone', label: 'Phone', component: FeatherIcons.FiPhone },
        { name: 'lock', label: 'Lock', component: FeatherIcons.FiLock },
        { name: 'unlock', label: 'Unlock', component: FeatherIcons.FiUnlock },
        { name: 'eye', label: 'Eye', component: FeatherIcons.FiEye },
        { name: 'eye-off', label: 'Eye Off', component: FeatherIcons.FiEyeOff },
        { name: 'edit', label: 'Edit', component: FeatherIcons.FiEdit },
        { name: 'trash', label: 'Trash', component: FeatherIcons.FiTrash },
        { name: 'trash-2', label: 'Trash 2', component: FeatherIcons.FiTrash2 },
        { name: 'download', label: 'Download', component: FeatherIcons.FiDownload },
        { name: 'upload', label: 'Upload', component: FeatherIcons.FiUpload },
        { name: 'share', label: 'Share', component: FeatherIcons.FiShare },
        { name: 'share-2', label: 'Share 2', component: FeatherIcons.FiShare2 },
        { name: 'bookmark', label: 'Bookmark', component: FeatherIcons.FiBookmark },
        { name: 'flag', label: 'Flag', component: FeatherIcons.FiFlag },
        { name: 'thumbs-up', label: 'Thumbs Up', component: FeatherIcons.FiThumbsUp },
        { name: 'thumbs-down', label: 'Thumbs Down', component: FeatherIcons.FiThumbsDown },
        { name: 'shopping-cart', label: 'Shopping Cart', component: FeatherIcons.FiShoppingCart },
        { name: 'shopping-bag', label: 'Shopping Bag', component: FeatherIcons.FiShoppingBag },
        { name: 'credit-card', label: 'Credit Card', component: FeatherIcons.FiCreditCard },
        { name: 'gift', label: 'Gift', component: FeatherIcons.FiGift },
        { name: 'award', label: 'Award', component: FeatherIcons.FiAward },
        { name: 'zap', label: 'Zap', component: FeatherIcons.FiZap },
        { name: 'sun', label: 'Sun', component: FeatherIcons.FiSun },
        { name: 'moon', label: 'Moon', component: FeatherIcons.FiMoon },
        { name: 'cloud', label: 'Cloud', component: FeatherIcons.FiCloud },
        { name: 'image', label: 'Image', component: FeatherIcons.FiImage },
        { name: 'file', label: 'File', component: FeatherIcons.FiFile },
        { name: 'folder', label: 'Folder', component: FeatherIcons.FiFolder },
        { name: 'save', label: 'Save', component: FeatherIcons.FiSave },
        { name: 'copy', label: 'Copy', component: FeatherIcons.FiCopy },
        { name: 'clipboard', label: 'Clipboard', component: FeatherIcons.FiClipboard },
        { name: 'filter', label: 'Filter', component: FeatherIcons.FiFilter },
        { name: 'refresh-cw', label: 'Refresh', component: FeatherIcons.FiRefreshCw },
        { name: 'maximize', label: 'Maximize', component: FeatherIcons.FiMaximize },
        { name: 'minimize', label: 'Minimize', component: FeatherIcons.FiMinimize },
        { name: 'play', label: 'Play', component: FeatherIcons.FiPlay },
        { name: 'pause', label: 'Pause', component: FeatherIcons.FiPause },
        { name: 'skip-forward', label: 'Skip Forward', component: FeatherIcons.FiSkipForward },
        { name: 'skip-back', label: 'Skip Back', component: FeatherIcons.FiSkipBack },
        { name: 'volume', label: 'Volume', component: FeatherIcons.FiVolume },
        { name: 'volume-2', label: 'Volume 2', component: FeatherIcons.FiVolume2 },
        { name: 'volume-x', label: 'Volume X', component: FeatherIcons.FiVolumeX },
    ];

    const brandIcons = [
        { name: 'globe', label: 'Globe', component: FaGlobe },
        { name: 'facebook', label: 'Facebook', component: FaFacebook },
        { name: 'twitter', label: 'Twitter', component: FaTwitter },
        { name: 'instagram', label: 'Instagram', component: FaInstagram },
        { name: 'google', label: 'Google', component: FaGoogle },
        { name: 'apple', label: 'Apple', component: FaApple },
        { name: 'linkedin', label: 'LinkedIn', component: FaLinkedin },
        { name: 'github', label: 'GitHub', component: FaGithub },
        { name: 'youtube', label: 'YouTube', component: FaYoutube },
        { name: 'tiktok', label: 'TikTok', component: FaTiktok },
    ];

    const allIcons = [...featherIcons, ...brandIcons];

    // Find selected icon
    const selectedIcon = allIcons.find(icon => icon.name === value) || allIcons[1]; // Default to star
    const SelectedIconComponent = selectedIcon.component;

    // Filter icons based on search query
    const filteredIcons = useMemo(() => {
        if (!searchQuery) return allIcons;
        const query = searchQuery.toLowerCase();
        return allIcons.filter(icon =>
            icon.name.toLowerCase().includes(query) ||
            icon.label.toLowerCase().includes(query)
        );
    }, [searchQuery]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between text-sm"
                >
                    <div className="flex items-center gap-2">
                        <SelectedIconComponent size={16} />
                        <span>{selectedIcon.label}</span>
                    </div>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
                <div className="p-2">
                    <Input
                        placeholder="Search icons..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="text-sm"
                    />
                </div>
                <ScrollArea className="h-[300px]">
                    <div className="p-2 space-y-1">
                        {filteredIcons.map((icon) => {
                            const IconComponent = icon.component;
                            const isSelected = value === icon.name;

                            return (
                                <button
                                    key={icon.name}
                                    onClick={() => {
                                        onChange(icon.name);
                                        setOpen(false);
                                        setSearchQuery('');
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isSelected
                                            ? 'bg-primary text-primary-foreground'
                                            : 'hover:bg-muted'
                                        }`}
                                >
                                    <IconComponent size={18} />
                                    <span>{icon.label}</span>
                                </button>
                            );
                        })}
                        {filteredIcons.length === 0 && (
                            <div className="text-center text-muted-foreground text-sm py-8">
                                No icons found
                            </div>
                        )}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};

export default IconPicker;
